import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Music2, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const r = await login(form.email, form.password);
    setBusy(false);
    if (r.ok) {
      nav(r.user.role === "admin" ? "/admin" : "/dashboard");
    } else {
      setErr(r.error);
    }
  };

  const fillAdmin = () => {
    setForm({ email: "admin@songniture.com", password: "" });
    setShowPw(false);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12 bg-black">
      <div className="w-full max-w-md bg-ink-900 border border-ink-700 p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 border border-gold flex items-center justify-center"><Music2 className="text-gold" /></div>
          <div className="font-heading text-3xl"><span className="text-white">Song</span><span className="text-gold">niture</span></div>
        </div>
        <h1 className="font-heading text-2xl mb-1">Welcome Back</h1>
        <p className="text-white/60 text-sm mb-6">Sign in to your portal</p>

        {/* Admin quick login banner — shown to anyone (helpful for the owner) */}
        <button
          type="button"
          onClick={fillAdmin}
          data-testid="login-admin-shortcut"
          className="w-full mb-5 flex items-center gap-3 border border-gold/40 bg-gold/5 hover:bg-gold/10 transition-colors px-4 py-3 text-left group"
        >
          <ShieldCheck className="text-gold" size={20} />
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.22em] uppercase text-gold font-bold">Admin / Owner Sign-In</div>
            <div className="text-white/70 text-xs mt-0.5">Click to fill the admin email (then enter your password)</div>
          </div>
        </button>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="label">Email</label>
            <input required type="email" autoComplete="username" className="input" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
          </div>
          <div>
            <label className="label">Password</label>
            <div className="relative">
              <input
                required
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                className="input pr-12"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="login-password-toggle"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-gold p-1"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {err && <div className="text-destructive text-sm" data-testid="login-error">{err}</div>}
          <button disabled={busy} className="btn-gold w-full justify-center" data-testid="login-submit">
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-sm text-white/60 text-center">
          New to Songniture?{" "}
          <Link to="/register" className="text-gold hover:underline" data-testid="login-to-register">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
